import { useEffect, useState } from 'react';
import { addError } from '@shared/utils/logger';
import type { Embedding, ThemeEmbeddings } from './search.types';

type PrecomputedEmbeddings = Readonly<{
  embeddingSizeBytes: number;
  keywords: string[];
  themes: Record<string, Readonly<[ReadonlyArray<number>, Readonly<[number, number, number]> | [], ReadonlyArray<number>]>>;
}>;

type PrecomputedEmbeddingsResult = Readonly<{
  themes: Map<string, ThemeEmbeddings> | undefined;
  embeddingMap: Map<string, Embedding>;
}>;

export function usePrecomputedEmbeddings(embeddingsStoreUrl: string): PrecomputedEmbeddingsResult {
  const [themesAndEmbeddings, setThemesAndEmbeddings] = useState<PrecomputedEmbeddingsResult>({
    themes: undefined,
    embeddingMap: new Map<string, Embedding>(),
  });

  useEffect(() => {
    let didCancel = false;

    void (async () => {
      try {
        const [jsonRes, binRes] = await Promise.all([fetch(`${embeddingsStoreUrl}/themes.json`), fetch(`${embeddingsStoreUrl}/themes.bin`)]);

        if (!jsonRes.ok) {
          throw new Error('Failed to fetch embeddings store JSON');
        }

        if (!binRes.ok) {
          throw new Error('Failed to fetch embeddings store BIN');
        }

        const [bundle, bigBuffer] = (await Promise.all([jsonRes.json(), binRes.arrayBuffer()])) as [PrecomputedEmbeddings, ArrayBuffer];

        const embeddings = bundle.keywords.map((_, i) => {
          const keywordBuffer = bigBuffer.slice(i * bundle.embeddingSizeBytes, (i + 1) * bundle.embeddingSizeBytes);
          return Array.from(new Float32Array(keywordBuffer)) as Embedding;
        });

        const themes = new Map(
          // Object.entries is >95% the compat table is being updated soon.
          // eslint-disable-next-line compat/compat
          Object.entries(bundle.themes).map(([id, [conceptIndexes, colorIndexes, customIndexes]]) => {
            return [
              id,
              {
                concepts: conceptIndexes.map(i => embeddings[i]),
                colors: colorIndexes.length === 0 ? ([] as []) : (colorIndexes.map(i => embeddings[i]) as Readonly<[Embedding, Embedding, Embedding]> | []),
                customs: customIndexes.map(i => embeddings[i]),
              },
            ];
          }),
        );

        if (!didCancel) {
          setThemesAndEmbeddings({
            themes,
            embeddingMap: new Map(bundle.keywords.map((keyword, i) => [keyword, embeddings[i]])),
          });
        }
      } catch (error) {
        addError(error, { context: 'usePrecomputedEmbeddings', embeddingsStoreUrl });
      }
    })();

    return () => {
      didCancel = true;
    };
  }, [embeddingsStoreUrl]);

  return themesAndEmbeddings;
}
